import { useState } from "react";
import moment, { Moment } from "moment";
import { useQuery, useQueryClient, QueryClient } from "@tanstack/react-query";
// Permissions
import actions from "@/Constants/actionsList";
// MUI
import { Grid, Paper, Skeleton, Stack, Typography } from "@mui/material";
// Images
import emptyTable from "@/assets/images/commons/empty_table.png";
// API
import { useAuthorization } from "@/Common/hooks/Authorization/Authorization";
import { getSalesPerformanceReportVersion } from "@/Common/api/salesPerformance/queries";
// Components
import SalesPerformanceFormatsVersion from "./SalesPerformanceFormatsVersion";
import EmptyBody from "@/Layouts/components/Basics/EmptyBody";
import DateTimePicker from "@/Layouts/components/DateTimePicker/DateTimePicker";

// Types
import {
	ISalesPerformanceReportVersion,
	ISalesPerformanceReportFormatsModelProps,
} from "@/Settings/types/SalesPerformanceReportFormats/model";
import Title from "../SystemSettings/common/Title";
import { DATE_FORMAT } from "@/Constants/date";

const newReportVersion = (createdBy: string, locationId: string, title: string): ISalesPerformanceReportVersion => {
	return {
		locationId,
		createdBy,
		title,
		_id: "new",
		effectiveDateTime: null,
		lastModifiedBy: createdBy,
		createdOn: moment().toDate(), // add this line
		lastModifiedOn: moment().toDate(), // add this line
		locked: false, // add this line
		saleDate: "JRNL-DATE",
		saleAmount: "SALE-AMT",
		dealNumber: "REF NO",
		customerName: "CUST-NAME",
		customerNumber: "CUST-NO",
		stockNumber: "STK-NO",
		description: "DESC",
		saleType: "SALE-TYPE",
		dealType: "SALE-TYPE",
		units: "UNITS",
		salesEmployeeId: "SLM-NO",
		financeEmployeeId: "FIN-MGR-NO",
		salesManagerId: "SLS-NO",
		frontProducts: [],
		backProducts: [],
	};
};

const validateVersion = (modelVersion: ISalesPerformanceReportVersion) => {
	let isValid = true;
	const validateProduct = (product: { label?: unknown; reportField?: unknown }) =>
		typeof product.label === "string" && typeof product.reportField === "string";
	const validateProducts = (products: Array<{ label?: unknown; reportField?: unknown }>) => products.every(validateProduct);

	const isInvalidKey = (key: keyof ISalesPerformanceReportVersion) => {
		const keyAsString = key as string;
		if (["frontProducts", "backProducts"].includes(keyAsString)) {
			const value = modelVersion[key];
			if (Array.isArray(value)) {
				return !validateProducts(value);
			}
			return true; // Consider invalid if not an array, adjust logic as needed
		}
		return !modelVersion[key] && keyAsString !== "locked";
	};

	try {
		isValid = !Object.keys(modelVersion).some((key) => isInvalidKey(key as keyof ISalesPerformanceReportVersion));
	} catch (error) {
		isValid = false;
	}

	return {
		isValid,
		version: modelVersion,
	};
};

const useSalesPerformanceReport = (props: Readonly<ISalesPerformanceReportFormatsModelProps>) => {
	const queryClient = useQueryClient();
	const { useAuth } = useAuthorization();
	const EDIT_SALES_PERFORMANCE_REPORT_FORMATS = useAuth(actions.EDIT_SALES_PERFORMANCE_REPORT_FORMATS);

	const { data: model, isLoading } = useQuery({
		queryKey: ["salesPerformanceReportVersion", props.location?.locationId, props.model?._id],
		queryFn: getReportVersion(props),
		enabled: !!props.location?.locationId && !!props.model?._id,
		staleTime: Infinity,
	});

	return { queryClient, EDIT_SALES_PERFORMANCE_REPORT_FORMATS, model, isLoading };
};

const getReportVersion = (props: Readonly<ISalesPerformanceReportFormatsModelProps>) => async () => {
	if (props.model?._id === "new" && props.user?._id && props.location?.locationId) {
		return newReportVersion(props.user._id, props.location.locationId, props?.title);
	}

	return getSalesPerformanceReportVersion(props.location?.locationId, props.model?._id, true);
};

const useQueryData = (props: Readonly<ISalesPerformanceReportFormatsModelProps>) => {
	const staleTime = props.model?._id === "new" ? 0 : Infinity;
	return useQuery({
		queryKey: ["salesPerformanceReportVersion", props.location?.locationId, props.model?._id],
		queryFn: getReportVersion(props),
		enabled: !!props.location?.locationId && !!props.model?._id,
		staleTime,
	});
};

const updateEffectiveDateTime = (
	dateTime: Moment,
	queryClient: QueryClient,
	props: Readonly<ISalesPerformanceReportFormatsModelProps>,
	model: ISalesPerformanceReportVersion
) => {
	queryClient.setQueryData(
		["salesPerformanceReportVersion", props.location?.locationId, model?._id],
		(data: ISalesPerformanceReportVersion | {}) => {
			if ("effectiveDateTime" in data && dateTime.format) {
				const format = dateTime.format(DATE_FORMAT.usDateTimeWithZeroedSeconds);
				const effectiveDate = new Date(format);
				data.effectiveDateTime = effectiveDate;
			}
			return data;
		}
	);
};

const updateModelVersion = (
	updatedModel: Partial<ISalesPerformanceReportVersion>,
	queryClient: QueryClient,
	props: Readonly<ISalesPerformanceReportFormatsModelProps>,
	model: ISalesPerformanceReportVersion
) => {
	queryClient.setQueryData(
		["salesPerformanceReportVersion", props.location?.locationId, model?._id],
		(data: ISalesPerformanceReportVersion | {}) => {
			if (data && "_id" in data) {
				return {
					...data,
					...updatedModel,
				};
			}
			return data;
		}
	);
};

const isEmptygOrLoading = (
	props: Readonly<ISalesPerformanceReportFormatsModelProps>,
	isLoading: boolean,
	model: ISalesPerformanceReportVersion
) => {
	return Boolean(!props.loading && !isLoading && !model);
};

const isCurrentLoading = (props: Readonly<ISalesPerformanceReportFormatsModelProps>, isLoading: boolean) => {
	return Boolean(props.loading ?? isLoading);
};

const isEditable = (EDIT_SALES_PERFORMANCE_REPORT_FORMATS: string, model: ISalesPerformanceReportVersion) => {
	return Boolean(EDIT_SALES_PERFORMANCE_REPORT_FORMATS && !model?.locked);
};

const getEffectiveDateTime = (model: ISalesPerformanceReportVersion) => {
	return model?.effectiveDateTime ? moment(model.effectiveDateTime) : null;
};

export default function SalesPerformanceReportFormatsModel(props: Readonly<ISalesPerformanceReportFormatsModelProps>) {
	const [datePayPeriods, setDatePayPeriods] = useState<{ startDate: string }[] | null>(null);
	const { queryClient, EDIT_SALES_PERFORMANCE_REPORT_FORMATS } = useSalesPerformanceReport(props);
	const { data: model, isLoading } = useQueryData(props);

	const handleUpdateEffectiveDateTime = (dateTime: Moment) => {
		updateEffectiveDateTime(dateTime, queryClient, props, model);
		propagate();
	};

	const handleShouldDisableDate = (date: Moment) => {
		const dateDay = date.toISOString().split("T")[0];

		if (!datePayPeriods && model?.datePeriods) {
			setDatePayPeriods(model.datePeriods);
		}

		const periodsToCheck = model?.datePeriods || datePayPeriods;

		if (periodsToCheck) {
			return periodsToCheck.every((period: { startDate: string }) => {
				const startDateDay = period.startDate.split("T")[0];
				return startDateDay !== dateDay;
			});
		}

		return true;
	};

	const handleUpdateModelVersion = (updatedModel: Partial<ISalesPerformanceReportVersion>) => {
		updateModelVersion(updatedModel, queryClient, props, model);
		propagate();
	};

	const propagate = () => {
		const queryData = ["salesPerformanceReportVersion", props.location?.locationId, props.model?._id];
		queryClient.setQueryData(queryData, (data: ISalesPerformanceReportVersion | {}) => {
			if ("datePeriods" in data) {
				delete data["datePeriods"];
			}
			if (data && "_id" in data) {
				const validation = validateVersion(data);
				if (validation.isValid && props.onActionUpdate) {
					props.onActionUpdate(validation.version);
				}
				return data;
			}
			return null;
		});
	};

	return (
		<Grid container mt={-1} style={{ width: "100%" }}>
			{isEmptygOrLoading(props, isLoading, model) ? (
				<EmptyBody
					image={emptyTable}
					title={"No Sales Performance Report Version to show"}
					bodyHeight={"510px"}
					sx={{
						boxShadow: "none !important",
					}}
				/>
			) : (
				<>
					<Grid container direction={"row"} alignItems="center">
						<Grid item sx={{ flexGrow: 1 }}>
							<Paper elevation={0} sx={{ backgroundColor: "transparent", width: "100%" }}>
								{!props?.model?.title || isCurrentLoading(props, isLoading) ? (
									<Stack direction={"row"} spacing={1} alignItems={"center"} mt={"5px"}>
										<Skeleton variant="rectangular" width={380} height={30} sx={{ borderRadius: "6px" }} />
									</Stack>
								) : (
									<Title title={props?.model?.title} />
								)}
							</Paper>
						</Grid>
						<Grid item>
							<Paper
								elevation={0}
								style={{ backgroundColor: "transparent", marginLeft: "10px", display: "flex", alignItems: "center" }}
							>
								{isCurrentLoading(props, isLoading) ? (
									<Stack direction={"row"} spacing={1} alignItems={"center"} mt={"5px"}>
										<Skeleton variant="text" width={100} height={15} />
										<Skeleton variant="rectangular" width={180} height={30} sx={{ borderRadius: "6px" }} />
									</Stack>
								) : (
									<>
										<Typography
											className={"!font-sans !text-[14.5px] text-default"}
											sx={{ flexGrow: 1, marginRight: "10px" }}
										>
											Effective Date
										</Typography>
										<DateTimePicker
											disabled={!isEditable(EDIT_SALES_PERFORMANCE_REPORT_FORMATS, model)}
											minDateTime={moment()}
											showToolbar={true}
											defaultValue={getEffectiveDateTime(model)}
											inputFormat={DATE_FORMAT.fullAbbrTime12hPadded}
											placement={"bottom-end"}
											sx={{ maxWidth: "180px" }}
											onActionUpdate={handleUpdateEffectiveDateTime}
											views={["year", "month", "day"]}
											onShouldDisableDate={handleShouldDisableDate}
											error={!model?.effectiveDateTime}
										/>
									</>
								)}
							</Paper>
						</Grid>
					</Grid>
					<Grid container mt={2}>
						<Grid item xs={12}>
							<SalesPerformanceFormatsVersion
								title={props.title || ""}
								model={model}
								onActionUpdate={handleUpdateModelVersion}
								loading={props.loading ?? isLoading}
								disabled={
									Boolean(model?.locked) ||
									!EDIT_SALES_PERFORMANCE_REPORT_FORMATS ||
									!model?.effectiveDateTime ||
									Boolean(props.disabled)
								}
								locationId={props.location?.locationId ?? null}
							/>
						</Grid>
					</Grid>
				</>
			)}
		</Grid>
	);
}
